import React from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as TargetArrowIcon } from '@repo/common/assets/icons/24px/icon_24px_target-arrow_outline.svg';

export const RECOMMEND_ACTION_PRESETS = [
  {
    id: 'preset-korean',
    title: 'Business Korean Writing',
    description: 'Learn to write professional Korean emails and documents',
    buttonLabel: 'Start now',
  },
  {
    id: 'preset-portfolio',
    title: 'Portfolio Building',
    description: 'Structure your work experience into measurable impact statements',
    buttonLabel: 'Start now',
  },
  {
    id: 'preset-role',
    title: 'Role Foundation',
    description: 'Understand core responsibilities of your target role in Korea',
    buttonLabel: 'Start now',
  },
  {
    id: 'preset-tools',
    title: 'Tool Mastery',
    description: 'Master the key tools used in your industry',
    buttonLabel: 'Start now',
  },
  {
    id: 'preset-interview',
    title: 'Professional Storytelling',
    description: 'Craft compelling career narratives for interviews',
    buttonLabel: 'Start now',
  },
];

const FIXED_RECOMMEND_ACTION_COUNT = 4;

const normalizeFixedRecommendActions = (items) => {
  const sourceItems = Array.isArray(items) && items.length > 0
    ? items
    : RECOMMEND_ACTION_PRESETS;

  return Array.from({ length: FIXED_RECOMMEND_ACTION_COUNT }, (_, index) => ({
    ...RECOMMEND_ACTION_PRESETS[index],
    ...sourceItems[index],
  }));
};

function RecommendActionCard({
  title = 'Module name',
  moduleName,
  description = 'To improve application readiness',
  helperText,
  buttonLabel = 'Start now',
  ctaLabel,
  onClick,
  to,
  path,
  href,
  url,
  className = '',
}) {
  const isClickable = typeof onClick === 'function';
  const displayTitle = title || moduleName;
  const displayDescription = description || helperText;
  const displayButtonLabel = ctaLabel || buttonLabel;
  const destination = to || path || href || url;
  const canNavigate = isClickable || Boolean(destination);

  const handleClick = () => {
    if (canNavigate) onClick?.();
  };

  const handleKeyDown = (event) => {
    if (!canNavigate) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role={canNavigate ? 'button' : undefined}
      tabIndex={canNavigate ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={twMerge(
        'flex min-h-[110px] w-full shrink-0 items-center gap-4 rounded-[30px] border border-grayscale-gray02 bg-white/50 backdrop-blur-[25px] transition-colors',
        'px-[24px] py-[16px] tabletV:px-[30px] tabletV:py-6',
        canNavigate
          ? 'cursor-pointer hover:bg-white/70 active:bg-white/80 focus:outline-none focus:ring-2 focus:ring-green-deep02/40'
          : '',
        className
      )}
    >
      <div className='flex min-w-0 flex-1 flex-col items-start gap-0.5'>
        <p className='w-full truncate typo-b16Strong text-grayscale-gray09'>
          {displayTitle}
        </p>
        <p className='w-full truncate typo-b14 text-grayscale-gray04'>
          {displayDescription}
        </p>
      </div>

      <button
        type='button'
        onClick={(event) => {
          event.stopPropagation();
          handleClick();
        }}
        disabled={!canNavigate}
        className={twMerge(
          'flex h-[38px] shrink-0 items-center justify-center whitespace-nowrap rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 px-3 py-[10px] text-grayscale-gray07 transition-colors disabled:cursor-default',
          canNavigate
            ? 'cursor-pointer hover:bg-grayscale-gray01 active:bg-grayscale-gray02'
            : ''
        )}
      >
        <span className='typo-b14Strong'>{displayButtonLabel}</span>
      </button>
    </div>
  );
}

function RecommendActions({
  items = RECOMMEND_ACTION_PRESETS,
  onActionClick,
  className = '',
}) {
  const navigate = useNavigate();
  const fixedItems = normalizeFixedRecommendActions(items);

  const moveToDestination = (destination) => {
    if (!destination) return;

    if (/^https?:\/\//.test(destination)) {
      window.location.assign(destination);
      return;
    }

    navigate(destination);
  };

  const handleActionClick = (item, index) => {
    const destination = item.to || item.path || item.href || item.url;

    if (onActionClick) {
      onActionClick(item, index);
      return;
    }

    moveToDestination(destination);
  };

  return (
    <section
      className={twMerge(
        'flex w-full flex-col items-start gap-5 rounded-[50px] desktopL:h-full',
        className
      )}
    >
      <div className='flex items-start gap-1.5 px-5'>
        <span className='flex h-[30px] shrink-0 items-center text-icon-primary'>
          <TargetArrowIcon className='size-6' />
        </span>
        <h2 className='typo-b20Strong whitespace-nowrap text-text-primary'>
          Increase your score
        </h2>
      </div>

      <div className='flex w-full flex-1 items-start rounded-[30px] border border-grayscale-gray02 bg-[rgba(236,233,228,0.4)] tabletV:min-h-[492px] tabletV:overflow-hidden tabletV:rounded-[40px] desktopL:min-h-0'>
        <div className='flex min-w-0 flex-1 flex-col items-start p-[15px] tabletV:h-full tabletV:overflow-hidden'>
          <div className='flex w-full flex-col items-start gap-[15px] rounded-[24px] tabletV:flex-1 tabletV:overflow-hidden tabletV:rounded-[30px]'>
            {fixedItems.map((item, index) => (
              <RecommendActionCard
                key={item.id || `${item.title || item.moduleName}-${index}`}
                title={item.title}
                moduleName={item.moduleName}
                description={item.description}
                helperText={item.helperText}
                buttonLabel={item.buttonLabel}
                ctaLabel={item.ctaLabel}
                to={item.to}
                path={item.path}
                href={item.href}
                url={item.url}
                onClick={() => {
                  if (item.onClick) {
                    item.onClick(item, index);
                    return;
                  }

                  handleActionClick(item, index);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export { RecommendActionCard };
export default RecommendActions;
